import React, { useState, useEffect, useRef } from 'react';
import Layout from '@/components/Layout';
import { useAuth } from '@/contexts/AuthContext';
import axios from '@/utils/axiosConfig';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { TrendingUp, Users, FileText, Clock, CheckCircle, XCircle, AlertCircle, Search, DollarSign } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';

const WorkflowTracker = () => {
  const { user, token } = useAuth();
  const [files, setFiles] = useState([]);
  const [filteredFiles, setFilteredFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const API = `${BACKEND_URL}/api`;

  // Protection contre les mises à jour sur composant démonté
  const isMountedRef = useRef(true);
  useEffect(() => {
    isMountedRef.current = true;
    return () => { isMountedRef.current = false; };
  }, []);

  // Workflow steps for immigration process INCLUDING PAYMENTS
  const workflowSteps = [
    { id: 1, name: 'Consultation initiale', status: 'consultation', type: 'process' },
    { id: 2, name: 'Devis & Paiement initial (30%)', status: 'payment_1', type: 'payment', paymentNumber: 1 },
    { id: 3, name: 'Collecte documents', status: 'documents_collection', type: 'process' },
    { id: 4, name: 'Préparation dossier', status: 'preparation', type: 'process' },
    { id: 5, name: 'Paiement intermédiaire (40%)', status: 'payment_2', type: 'payment', paymentNumber: 2 },
    { id: 6, name: 'Révision qualité', status: 'quality_review', type: 'process' },
    { id: 7, name: 'Soumission IRCC', status: 'submitted', type: 'process' },
    { id: 8, name: 'Paiement final (30%)', status: 'payment_3', type: 'payment', paymentNumber: 3 },
    { id: 9, name: 'Suivi traitement', status: 'processing', type: 'process' },
    { id: 10, name: 'Demandes additionnelles', status: 'additional_info', type: 'process' },
    { id: 11, name: 'Décision finale', status: 'decision', type: 'process' },
    { id: 12, name: 'Post-approbation', status: 'post_approval', type: 'process' },
    { id: 13, name: 'Dossier complété', status: 'completed', type: 'process' }
  ];

  useEffect(() => {
    fetchFiles();
  }, []);

  useEffect(() => {
    filterFiles();
  }, [files, searchTerm, statusFilter]);

  const fetchFiles = async () => {
    try {
      const response = await axios.get(`${API}/files`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Enrich files with workflow data AND payment data
      const enrichedFiles = await Promise.all(
        response.data.map(async (file) => {
          try {
            // Fetch client info
            const clientResponse = await axios.get(`${API}/users/${file.client_id}`, {
              headers: { Authorization: `Bearer ${token}` }
            });
            
            // Fetch payments for this file
            const paymentsResponse = await axios.get(`${API}/payments`, {
              headers: { Authorization: `Bearer ${token}` }
            });
            const filePayments = paymentsResponse.data.filter(p => p.file_id === file.id);
            
            // Sort payments by installment number
            filePayments.sort((a, b) => (a.installment_number || 0) - (b.installment_number || 0));
            
            return {
              ...file,
              client_name: clientResponse.data.full_name,
              client_email: clientResponse.data.email,
              payments: filePayments
            };
          } catch {
            return {
              ...file,
              client_name: 'Client inconnu',
              client_email: '',
              payments: []
            };
          }
        })
      );
      
      setFiles(enrichedFiles);
    } catch (error) {
      if (isMountedRef.current) {
        toast.error('Erreur lors du chargement des dossiers');
      }
      console.error('Error fetching files:', error);
    } finally {
      if (isMountedRef.current) setLoading(false);
    }
  };

  const filterFiles = () => {
    let filtered = [...files];

    if (searchTerm) {
      filtered = filtered.filter(file =>
        file.client_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        file.file_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        file.client_email?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (statusFilter !== 'all') {
      filtered = filtered.filter(file => file.status === statusFilter);
    }

    if (isMountedRef.current) setFilteredFiles(filtered);
  };

  const getProgressPercentage = (status) => {
    const statusMap = {
      'consultation': 10,
      'documents_collection': 20,
      'preparation': 30,
      'quality_review': 40,
      'submitted': 50,
      'processing': 60,
      'additional_info': 70,
      'decision': 80,
      'post_approval': 90,
      'completed': 100
    };
    return statusMap[status] || 0;
  };

  const getStatusColor = (status) => {
    const colorMap = {
      'consultation': 'bg-blue-100 text-blue-800',
      'documents_collection': 'bg-yellow-100 text-yellow-800',
      'preparation': 'bg-orange-100 text-orange-800',
      'quality_review': 'bg-purple-100 text-purple-800',
      'submitted': 'bg-indigo-100 text-indigo-800',
      'processing': 'bg-cyan-100 text-cyan-800',
      'additional_info': 'bg-pink-100 text-pink-800',
      'decision': 'bg-teal-100 text-teal-800',
      'post_approval': 'bg-green-100 text-green-800',
      'completed': 'bg-emerald-100 text-emerald-800'
    };
    return colorMap[status] || 'bg-gray-100 text-gray-800';
  };

  const getStatusIcon = (status) => {
    if (status === 'completed') return <CheckCircle className="h-5 w-5 text-green-600" />;
    if (status === 'additional_info') return <AlertCircle className="h-5 w-5 text-yellow-600" />;
    return <Clock className="h-5 w-5 text-blue-600" />;
  };

  const getStatusLabel = (status) => {
    const step = workflowSteps.find(s => s.status === status);
    return step ? step.name : status;
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-red-600"></div>
        </div>
      </Layout>
    );
  }

  // Calculate statistics
  const stats = {
    total: files.length,
    inProgress: files.filter(f => f.status !== 'completed').length,
    completed: files.filter(f => f.status === 'completed').length,
    needsAttention: files.filter(f => f.status === 'additional_info').length
  };

  return (
    <Layout>
      <div className="space-y-8">
        {/* Header */}
        <div>
          <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-2">
            Suivi de Workflow Client
          </h1>
          <p className="text-lg text-gray-600">
            Suivez l'état d'avancement de tous les dossiers clients en temps réel
          </p>
        </div>

        {/* Statistics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <Card className="border-0 shadow-xl bg-gradient-to-br from-blue-50 to-blue-100">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600 mb-1">Total Dossiers</p>
                  <p className="text-3xl font-bold text-gray-900">{stats.total}</p>
                </div>
                <div className="p-3 rounded-lg bg-blue-600">
                  <FileText className="h-8 w-8 text-white" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-xl bg-gradient-to-br from-yellow-50 to-yellow-100">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600 mb-1">En cours</p>
                  <p className="text-3xl font-bold text-gray-900">{stats.inProgress}</p>
                </div>
                <div className="p-3 rounded-lg bg-yellow-600">
                  <Clock className="h-8 w-8 text-white" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-xl bg-gradient-to-br from-green-50 to-green-100">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600 mb-1">Complétés</p>
                  <p className="text-3xl font-bold text-gray-900">{stats.completed}</p>
                </div>
                <div className="p-3 rounded-lg bg-green-600">
                  <CheckCircle className="h-8 w-8 text-white" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-xl bg-gradient-to-br from-red-50 to-red-100">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600 mb-1">Attention requise</p>
                  <p className="text-3xl font-bold text-gray-900">{stats.needsAttention}</p>
                </div>
                <div className="p-3 rounded-lg bg-red-600">
                  <AlertCircle className="h-8 w-8 text-white" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card className="border-0 shadow-xl">
          <CardContent className="p-6">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <Input
                    placeholder="Rechercher par nom, email, numéro de dossier..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                  />
                </div>
              </div>
              <div className="flex gap-2 flex-wrap">
                <Button
                  variant={statusFilter === 'all' ? 'default' : 'outline'}
                  onClick={() => setStatusFilter('all')}
                  size="sm"
                >
                  Tous
                </Button>
                <Button
                  variant={statusFilter === 'processing' ? 'default' : 'outline'}
                  onClick={() => setStatusFilter('processing')}
                  size="sm"
                >
                  En traitement
                </Button>
                <Button
                  variant={statusFilter === 'additional_info' ? 'default' : 'outline'}
                  onClick={() => setStatusFilter('additional_info')}
                  size="sm"
                >
                  Info requise
                </Button>
                <Button
                  variant={statusFilter === 'completed' ? 'default' : 'outline'}
                  onClick={() => setStatusFilter('completed')}
                  size="sm"
                >
                  Complétés
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Légende du Workflow */}
        <Card className="border-0 shadow-lg bg-gradient-to-r from-blue-50 to-purple-50">
          <CardContent className="p-4">
            <div className="flex items-center gap-6 flex-wrap text-sm">
              <div className="font-semibold text-gray-700">Légende :</div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-blue-100 border-2 border-blue-300 rounded"></div>
                <span>Étape complétée</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-blue-100 border-2 border-blue-500 rounded animate-pulse"></div>
                <span>Étape en cours</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-gray-50 border-2 border-gray-200 rounded"></div>
                <span>En attente</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-green-100 border-2 border-green-500 rounded"></div>
                <DollarSign className="h-3 w-3 -ml-1" />
                <span>Paiement payé</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-yellow-100 border-2 border-yellow-400 rounded"></div>
                <DollarSign className="h-3 w-3 -ml-1" />
                <span>Paiement en attente</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-red-100 border-2 border-red-500 rounded animate-pulse"></div>
                <DollarSign className="h-3 w-3 -ml-1" />
                <span>Paiement en retard</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Files List with Workflow Progress */}
        <div className="grid grid-cols-1 gap-6">
          {filteredFiles.length === 0 ? (
            <Card className="border-0 shadow-xl">
              <CardContent className="p-12 text-center text-gray-500">
                <FileText className="h-16 w-16 mx-auto mb-4 opacity-50" />
                <p className="text-lg">Aucun dossier trouvé</p>
              </CardContent>
            </Card>
          ) : (
            filteredFiles.map((file) => {
              const progress = getProgressPercentage(file.status);
              const currentStep = workflowSteps.find(s => s.status === file.status);
              const currentStepIndex = currentStep ? currentStep.id - 1 : 0;

              return (
                <Card key={file.id} className="border-0 shadow-xl hover:shadow-2xl transition-shadow">
                  <CardContent className="p-6">
                    {/* Header */}
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="text-xl font-bold text-gray-900">{file.client_name}</h3>
                          <Badge variant="outline" className="text-xs">
                            {file.file_number}
                          </Badge>
                          <Badge className={getStatusColor(file.status)}>
                            {getStatusIcon(file.status)}
                            <span className="ml-1">{getStatusLabel(file.status)}</span>
                          </Badge>
                        </div>
                        <p className="text-sm text-gray-600">{file.client_email}</p>
                        <p className="text-xs text-gray-500 mt-1">
                          Créé le {format(new Date(file.created_at), 'dd/MM/yyyy')}
                        </p>
                      </div>
                      <div className="text-right">
                        <div className="text-3xl font-bold text-blue-600 mb-1">{progress}%</div>
                        <p className="text-xs text-gray-500">Progression</p>
                      </div>
                    </div>

                    {/* Progress Bar */}
                    <div className="mb-4">
                      <div className="w-full bg-gray-200 rounded-full h-3">
                        <div
                          className="bg-gradient-to-r from-blue-500 to-green-500 h-3 rounded-full transition-all duration-500"
                          style={{ width: `${progress}%` }}
                        ></div>
                      </div>
                    </div>

                    {/* Workflow Steps */}
                    <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-13 gap-2">
                      {workflowSteps.map((step, index) => {
                        let stepStatus = 'pending';
                        let stepIcon = null;
                        let bgColor = 'bg-gray-50 text-gray-400 border-2 border-gray-200';
                        
                        // Déterminer le statut de l'étape
                        if (step.type === 'payment') {
                          // C'est une étape de paiement
                          const payment = file.payments?.find(p => p.installment_number === step.paymentNumber);
                          
                          if (payment) {
                            if (payment.status === 'paye') {
                              stepStatus = 'completed';
                              bgColor = 'bg-green-100 text-green-800 border-2 border-green-500';
                              stepIcon = <CheckCircle className="h-4 w-4 mx-auto mb-1 text-green-600" />;
                            } else if (payment.status === 'en_retard') {
                              stepStatus = 'overdue';
                              bgColor = 'bg-red-100 text-red-800 border-2 border-red-500 animate-pulse';
                              stepIcon = <AlertCircle className="h-4 w-4 mx-auto mb-1 text-red-600" />;
                            } else {
                              stepStatus = 'current';
                              bgColor = 'bg-yellow-100 text-yellow-800 border-2 border-yellow-400';
                              stepIcon = <Clock className="h-4 w-4 mx-auto mb-1 text-yellow-600" />;
                            }
                          } else {
                            stepStatus = 'pending';
                          }
                        } else {
                          // Étape de processus normale
                          const isCompleted = index < currentStepIndex;
                          const isCurrent = index === currentStepIndex;
                          
                          if (isCompleted) {
                            stepStatus = 'completed';
                            bgColor = 'bg-blue-100 text-blue-800 border-2 border-blue-300';
                            stepIcon = <CheckCircle className="h-4 w-4 mx-auto mb-1 text-blue-600" />;
                          } else if (isCurrent) {
                            stepStatus = 'current';
                            bgColor = 'bg-blue-100 text-blue-800 border-2 border-blue-500 animate-pulse';
                          }
                        }

                        return (
                          <div
                            key={step.id}
                            className={`p-2 rounded-lg text-center text-xs transition-all relative ${bgColor}`}
                          >
                            {step.type === 'payment' && (
                              <DollarSign className="h-3 w-3 absolute top-1 right-1 opacity-70" />
                            )}
                            {stepIcon}
                            <div className="font-semibold mb-1">{step.id}</div>
                            <div className="text-[10px] leading-tight">{step.name}</div>
                            {step.type === 'payment' && file.payments?.find(p => p.installment_number === step.paymentNumber) && (
                              <div className="text-[9px] mt-1 font-bold">
                                {file.payments.find(p => p.installment_number === step.paymentNumber).amount.toFixed(0)}$
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>

                    {/* Action Button */}
                    <div className="mt-4 flex justify-end">
                      <a href={`/files/${file.id}`}>
                        <Button size="sm" variant="outline">
                          <FileText className="mr-2 h-4 w-4" />
                          Voir le dossier
                        </Button>
                      </a>
                    </div>
                  </CardContent>
                </Card>
              );
            })
          )}
        </div>
      </div>
    </Layout>
  );
};

export default WorkflowTracker;
